import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Check, X, Eye, MoreVertical, Pencil, Trash2, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { format } from 'date-fns';

const InvoicesTable = ({
  invoices,
  sortBy,
  sortOrder,
  onSort,
  onTogglePaid,
  onView,
  onEdit,
  onDelete,
  canDeleteInvoice
}) => {
  const getSortIcon = (column) => {
    if (sortBy !== column) return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  const SortableHeader = ({ column, children }) => (
    <TableHead 
      className="cursor-pointer hover:bg-gray-50 select-none"
      onClick={() => onSort(column)}
      data-testid={`sort-${column}`}
    >
      <div className="flex items-center">
        {children}
        {getSortIcon(column)}
      </div>
    </TableHead>
  );

  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader column="invoiceNumber">Invoice #</SortableHeader>
              <SortableHeader column="clientName">Client</SortableHeader>
              <SortableHeader column="invoiceDate">Date</SortableHeader>
              <SortableHeader column="dueDate">Due Date</SortableHeader>
              <SortableHeader column="grandTotal">Amount</SortableHeader>
              <SortableHeader column="status">Status</SortableHeader>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((invoice) => {
              const paidAmount = invoice.paidAmount || 0;
              const grandTotal = invoice.grandTotal || 0;
              const remainingBalance = grandTotal - paidAmount;
              
              const getStatusColor = () => {
                if (invoice.status === 'paid') return 'bg-green-100 text-green-800';
                if (invoice.status === 'partial') return 'bg-orange-100 text-orange-800';
                return 'bg-yellow-100 text-yellow-800';
              };
              
              return (
                <TableRow key={invoice._id}>
                  <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                  <TableCell>{invoice.clientName}</TableCell>
                  <TableCell>
                    {invoice.invoiceDate && format(new Date(invoice.invoiceDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {invoice.dueDate && format(new Date(invoice.dueDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell className="font-medium">
                    <div>
                      ${grandTotal.toFixed(2)}
                      {invoice.status === 'partial' && (
                        <div className="text-xs text-gray-500">
                          Paid: ${paidAmount.toFixed(2)} | Due: ${remainingBalance.toFixed(2)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor()}>
                      {invoice.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onTogglePaid(invoice)}
                        className="gap-1"
                      >
                        {invoice.status === 'paid' ? (
                          <>
                            <X className="h-4 w-4" />
                            Reset
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4" />
                            {invoice.status === 'partial' ? 'Add Payment' : 'Pay'}
                          </>
                        )}
                      </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onView(invoice)}
                      className="gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(invoice)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit Invoice
                        </DropdownMenuItem>
                        {canDeleteInvoice && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => onDelete(invoice)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete Invoice
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
              );
            })}
            {invoices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                  No invoices found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default InvoicesTable;
